import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AuthLayout from '../../components/layout/AuthLayout';
import { useGetLeadQuery } from '../../api/leads.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { getInitials } from '../../utils/getInitials.js';
import BaseButton from '../../components/form/BaseButton.jsx';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { PERMISSION_LEADS } from '../../utils/constants.js';
import IconChecked from '../../components/images/IconChecked.jsx';
import IconRocket from '../../components/images/IconRocket.jsx';
import IconFullyOwned from '../../components/images/IconFullyOwned.jsx';
import IconPartiallyOwned from '../../components/images/IconPartiallyOwned.jsx';
import IconPartnerCompany from '../../components/images/IconPartnerCompany.jsx';
import IconBulb from '../../components/images/IconBulb.jsx';
import IconInfo from '../../components/images/IconInfo.jsx';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';
import MatchSkillsList from '../../components/misc/leads/MatchSkillsList.jsx';
import LeadStatusChangeModal from '../../components/misc/leads/LeadStatusChangeModal.jsx';
import { formatNumbers } from '../../utils/formatNumbers.js';
import BaseCard from '../../components/misc/BaseCard.jsx';

const getModelData = (model, prefix, lead) => {
    switch (model) {
        case 'Project':
            return lead[`${prefix}_project`];
        case 'Organization':
            return lead[`${prefix}_organization`];
        case 'Contact':
            return lead[`${prefix}_contact`];
        default:
            return null;
    }
};

const getModelLink = (model, prefix, lead) => {
    const id = lead[`${prefix}_id`];
    switch (model) {
        case 'Organization':
            return `/organizations/${id}`;
        case 'Project':
            return `/projects/${id}`;
        case 'Contact':
            return `/contacts/view/${id}`;
        default:
            return '#';
    }
};

const ModelCard = ({ model, data }) => {
    if (!data) return <div>No data available.</div>;

    switch (model) {
        case 'Project':
            return (
                <div className='text-center flex flex-col items-center'>
                    <div className='w-16 h-16 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-2xl flex-shrink-0 mb-2'>
                        <IconRocket className='stroke-theme1-primary w-8 h-8' />
                    </div>

                    <h2 className='text-[15px] text-theme1-primary font-bold mb-1'>{data.name}</h2>
                    <p className='text-xs text-theme1-text1'>{data.description}</p>
                </div>
            );
        case 'Organization':
            return (
                <div className='text-center flex flex-col items-center'>
                    <div className='w-16 h-16 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-2xl flex-shrink-0 mb-2'>
                        {getInitials(data.name)}
                    </div>
                    <h2 className='text-[15px] text-theme1-primary font-bold mb-1'>{data.name}</h2>
                    <div className='flex items-center flex-col gap-1 sm:gap-4 sm:flex-row'>
                        {data.industry && (
                            <div className='flex items-center'>
                                <p className='text-xs text-theme1-text1 font-bold'>{data.industry}</p>
                            </div>
                        )}
                        {data.ownership && (
                            <div className='flex items-center'>
                                {data.ownership === 'fully-owned' ? (
                                    <IconFullyOwned className='w-4 h-4 fill-theme1-text1 mr-1' />
                                ) : data.ownership === 'partially-owned' ? (
                                    <IconPartiallyOwned className='w-[15px] h-[15px] stroke-theme1-text1 mr-1' />
                                ) : (
                                    <IconPartnerCompany className='w-6 h-4 stroke-theme1-text1 mr-1' />
                                )}
                                <p className='text-xs text-theme1-text1 font-bold capitalize text-left'>
                                    {removeDashes(data.ownership)}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            );
        case 'Contact':
            return (
                <div className='text-center flex flex-col items-center'>
                    {data.avatar ? (
                        <img
                            src={data.avatar}
                            alt={`${data.first_name} ${data.last_name}`}
                            className='w-16 h-16 rounded-full mb-2'
                        />
                    ) : (
                        <div className='w-16 h-16 bg-theme1-pale rounded-full flex items-center justify-center text-theme1-primary text-2xl flex-shrink-0 mb-2'>
                            {getInitials(`${data.first_name} ${data.last_name}`)}
                        </div>
                    )}
                    <p className='text-[15px] text-theme1-primary font-bold mb-1'>
                        {data.first_name} {data.last_name}
                    </p>
                </div>
            );
        default:
            return null;
    }
};

const removeDashes = (text) => {
    return text ? text.replace(/-/g, ' ') : '';
};

const LeadsDetail = () => {
    const { permissions: userPermissions } = useCurrentUser();
    const { id } = useParams();
    const { data, isLoading } = useGetLeadQuery(id);

    const lead = data?.response?.lead;

    const [showDialog, setShowDialog] = useState(false);
    const [changeStatusVisible, setChangeStatusVisible] = useState(false);

    const handleChangeStatus = () => {
        setChangeStatusVisible(true);
    };

    if (isLoading) {
        return (
            <AuthLayout title='Leads Match Details' backRoute='/leads'>
                <ThreeDotsLoader className='mt-6' />
            </AuthLayout>
        );
    }

    if (!data) {
        return (
            <AuthLayout title='Leads Match Details' backRoute='/leads'>
                <div className='text-center mt-6'>Lead not found.</div>
            </AuthLayout>
        );
    }

    const getLeadFromModelNeeds = (lead) => {
        if (lead.from_model === 'Project') return lead.from_project.needs;
        if (lead.from_model === 'Contact') return lead.from_contact.needs;
        if (lead.from_model === 'Organization') return lead.from_organization.needs;

        return [];
    };

    return (
        <AuthLayout title={lead.to_model ? 'Leads Match Details' : 'Opportunity Details'} backRoute='/leads'>
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-10'>
                <div className='flex justify-between items-center mb-8'>
                    <div className='text-theme1-dark text-xl sm:text-[26px] font-bold'>
                        {lead.from_model}
                        {lead.to_model ? ' & ' + lead.to_model : ''}
                    </div>
                    {lead.to_model && <IconChecked className='fill-theme1-dark w-7 h-7' />}
                </div>
                <div className='flex gap-[18px] relative'>
                    {lead.to_model && (
                        <div className='w-11 h-11 bg-theme1-primary rounded-full flex items-center justify-center flex-shrink-0 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'>
                            <IconBulb className='stroke-white w-9 h-9' />
                        </div>
                    )}
                    <div className='w-1/2 border border-solid border-theme1-cardStroke bg-theme1-white p-4 sm:p-6 py-6 rounded-4xl'>
                        <Link to={getModelLink(lead.from_model, 'from', lead)}>
                            <ModelCard model={lead.from_model} data={getModelData(lead.from_model, 'from', lead)} />
                        </Link>
                    </div>
                    {lead.to_model && (
                        <div className='w-1/2 border border-solid border-theme1-cardStroke bg-theme1-white p-4 sm:p-6 py-6 rounded-4xl'>
                            <Link to={getModelLink(lead.to_model, 'to', lead)}>
                                <ModelCard model={lead.to_model} data={getModelData(lead.to_model, 'to', lead)} />
                            </Link>
                        </div>
                    )}
                </div>
                <div className='flex gap-[18px] relative mt-2'>
                    <div className='w-1/2 flex items-center justify-center'>
                        {lead.type === 'skill-to-need' ? (
                            <div className='text-base font-semibold text-theme1-dark'>Capabilities</div>
                        ) : (
                            <div className='text-base font-semibold text-theme1-dark'>Needs</div>
                        )}
                        <IconChecked className='fill-theme1-dark w-5 h-5 ml-1' />
                    </div>
                    {lead.to_model && (
                        <div className='w-1/2 flex items-center justify-center'>
                            {lead.type === 'skill-to-need' ? (
                                <div className='text-base font-semibold text-theme1-dark'>Needs</div>
                            ) : (
                                <div className='text-base font-semibold text-theme1-dark'>Capabilities</div>
                            )}
                            <IconChecked className='fill-theme1-dark w-5 h-5 ml-1' />
                        </div>
                    )}
                </div>
                {lead.to_model && (
                    <div className='border border-solid border-theme1-cardStroke bg-theme1-white p-4 sm:p-6 py-6 rounded-4xl mt-10 mb-10'>
                        <div className='border-b border-solid border-theme1-pale flex justify-between pb-6 items-center'>
                            <div className='flex items-center gap-2 sm:gap-4'>
                                <div className='font-extrabold text-[42px] sm:text-[60px] text-theme1-success leading-none'>
                                    {formatNumbers(Number(lead?.ranking))}
                                </div>
                                <div className='pt-[10px] sm:pt-1'>
                                    <span className='font-extrabold text-xl sm:text-2xl block leading-[12px] sm:leading-none'>
                                        Points
                                    </span>
                                    <span className='text-theme1-dark font-semibold text-[15px] sm:text-[17px] leading-none'>
                                        Ranking
                                    </span>
                                </div>
                            </div>
                            <Dialog
                                visible={showDialog}
                                style={{ width: '90vw' }}
                                onHide={() => setShowDialog(false)}
                                header={
                                    <div className='flex items-center gap-2'>
                                        <IconInfo className='stroke-theme1-info' />
                                        <span className='text-theme1-info font-bold text-base'>Ranking Legend</span>
                                    </div>
                                }
                                className='custom-dialog'
                            >
                                <div>
                                    <p>Criteria and scoring for ranking:</p>
                                    <ul className='list-disc pl-6'>
                                        <li>
                                            Number of matched skills (capabilities)
                                            <ul className='list-disc pl-4'>
                                                <li>
                                                    for each matched skill (child level): <strong>3 points</strong>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>
                                            history with organization (project statuses)
                                            <ul className='list-disc pl-4'>
                                                <li>
                                                    no of common projects:
                                                    <ul className='list-disc pl-4'>
                                                        <li>project status</li>
                                                        <li>
                                                            planned <strong>(1 point)</strong>
                                                        </li>
                                                        <li>
                                                            ongoing <strong>(3 points)</strong>
                                                        </li>
                                                        <li>
                                                            completed <strong>(5 points)</strong>
                                                        </li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </li>
                                        <li>
                                            Geographic proximity
                                            <ul className='list-disc pl-4'>
                                                <li>
                                                    Same country <strong>(5 points)</strong>
                                                </li>
                                                <li>
                                                    Same economic region <strong>(2 points)</strong>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </Dialog>
                            <span onClick={() => setShowDialog(true)} className='cursor-pointer'>
                                <IconInfo className='stroke-theme1-info' />
                            </span>
                        </div>
                        <div className='flex justify-between pt-6 items-center'>
                            <div className='text-center flex-1'>
                                <Badge
                                    value={lead.ranking_details.skills.points}
                                    severity='success'
                                    style={{ marginLeft: '60px' }}
                                ></Badge>
                                <div className='text-2xl text-theme1-primary2 font-bold'>
                                    {lead.ranking_details.skills.count}
                                </div>
                                <div className='text-theme1-primary2 text-lg'>Skills</div>
                            </div>

                            <div className='text-center flex-1'>
                                <Badge
                                    value={lead.ranking_details.projects.points}
                                    severity='success'
                                    style={{ marginLeft: '60px' }}
                                ></Badge>
                                <div className='text-2xl text-theme1-primary2 font-bold'>
                                    {lead.ranking_details.projects.count}
                                </div>
                                <div className='text-theme1-primary2 text-lg'>Projects</div>
                            </div>

                            <div className='text-center flex-1'>
                                <Badge
                                    value={lead.ranking_details.country.points}
                                    severity='success'
                                    style={{ marginLeft: '60px' }}
                                ></Badge>
                                <div className='text-2xl text-theme1-primary2 font-bold'>
                                    {lead.ranking_details.country.count}
                                </div>
                                <div className='text-theme1-primary2 text-lg'>Countries</div>
                            </div>
                        </div>
                    </div>
                )}
                <div
                    className={`flex justify-between items-center ${!lead.to_model ? 'pt-6' : ''} ${lead.from_model !== 'Organization' && lead.to_model !== 'Organization' ? 'mb-8' : 'mb-4'}`}
                >
                    <div className='text-theme1-dark text-xl sm:text-[26px] font-bold'>
                        {lead.to_model ? 'Matches' : 'Needs'}
                    </div>
                    {lead.to_model && <IconChecked className='fill-theme1-dark w-7 h-7' />}
                </div>
                {lead.to_model ? (
                    <MatchSkillsList lead={lead} />
                ) : (
                    <BaseCard className='border-0 m-0 p-0 pt-4 shadow-none w-auto'>
                        <div className='max-w-3xl w-full mx-auto px-4'>
                            <div className='flex items-center flex-wrap'>
                                {getLeadFromModelNeeds(lead).map((need) => (
                                    <span
                                        key={need.id}
                                        className='mr-2 mb-4 px-2 py-1 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white text-sm flex items-center'
                                    >
                                        {need.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </BaseCard>
                )}
                <div className='mt-10 flex justify-between items-center'>
                    <div className='flex-1 flex gap-x-2 flex-wrap'>
                        <span>Lead Status:</span>
                        <span className='font-bold capitalize'>{removeDashes(lead.status)}</span>
                    </div>

                    {!!userPermissions[PERMISSION_LEADS]?.update && (
                        <BaseButton
                            className='btn-link-primary !w-auto !text-base !p-2 !px-3'
                            onClick={handleChangeStatus}
                            textValue='Change status'
                        />
                    )}
                </div>
            </div>
            {changeStatusVisible && (
                <LeadStatusChangeModal
                    changeStatusVisible={changeStatusVisible}
                    setChangeStatusVisible={setChangeStatusVisible}
                    lead={lead}
                />
            )}
        </AuthLayout>
    );
};

export default LeadsDetail;
